/* istanbul ignore file */
import { Show } from 'framework/show';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import {
  DateField,
  EmailField,
  HtmlField,
  ListField,
  TextField,
  AmountField,
  BooleanOptionField,
} from 'framework/ui/field';
import { ReplaceUrlWithLink } from 'framework/utils';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { useOpportunityUrl } from 'domain/sales/opportunity/opportunityRouting';
import OpportunityEntityHeader from 'domain/sales/opportunity/shared/OpportunityEntityHeader';
import { AccountLinkField } from 'domain/shared/account';
import { useOpportunityId } from 'domain/shared/opportunity';
import useOpportunityService, {
  CLOSED_LOST,
  CLOSED_WON,
  DELETE_OPPORTUNITY,
  UPDATE_OPPORTUNITY,
} from 'domain/sales/opportunity/opportunityService';
import OpportunityStageField from 'domain/sales/opportunity/shared/field/OpportunityStageField';
import SingleOpportunityBreadcrumbs from 'domain/sales/opportunity/shared/SingleOpportunityBreadcrumbs';
import { LeadLinkField } from 'domain/shared/lead';
import ContactLinkField from 'domain/sales/contact/shared/field/ContactLinkField';
import OpportunityTypeField from 'domain/sales/opportunity/shared/field/OpportunityTypeField';
import OpportunityProbabilityField from 'domain/sales/opportunity/shared/field/OpportunityProbabilityField';
import OpportunityMenu from 'domain/sales/opportunity/shared/OpportunityMenu';
import OpportunityExpectedRevenueField from 'domain/sales/opportunity/shared/field/OpportunityExpectedRevenueField';
import EditOpportunityButton from 'domain/sales/opportunity/shared/button/EditOpportunityButton';
import { DeleteButton } from 'framework/ui/ui-builder';
import OpportunityPrimaryContactPhoneField from 'domain/sales/opportunity/shared/field/OpportunityPrimaryContactPhoneField';
import ClosedReasonField from '../shared/field/ClosedReasonField';
import OpportunityPipeline from 'domain/sales/opportunity/shared/OpportunityPipeline';
import OpportunityLinkField from 'domain/sales/opportunity/shared/field/OpportunityLinkField';
import NewProjectNameField from 'domain/sales/opportunity/shared/field/ProjectNameField';
import DocuSignEnvelopeBox from 'domain/sales/shared/docusign/envelope/DocuSignEnvelopeBox';
import { CompositeDocumentListField } from 'domain/shared/documentStorage';

const OpportunityViewPage = () => {
  const id = useOpportunityId();
  const { getById, deleteById } = useOpportunityService();
  const { getListUrl } = useOpportunityUrl();

  const buttons = [
    <EditOpportunityButton permissionToCheck={UPDATE_OPPORTUNITY} icon={<i className="fa fa-edit" />} />,
    <DeleteButton
      deleteMethod={() => deleteById(id)}
      displayMessage="Do you really want to delete this opportunity?"
      afterDelete={{
        redirectUrl: getListUrl(),
        successMessage: 'The Opportunity has been successfully deleted.',
        errorMessage: `Can't delete opportunity`,
      }}
      permissionToCheck={DELETE_OPPORTUNITY}
    />,
  ];

  const isClosedReasonVisible = (opportunity: any) =>
    opportunity.stage === CLOSED_WON || opportunity.stage === CLOSED_LOST;

  return (
    <Show getDataMethod={() => getById(id)}>
      <ShowPageLayout
        headerButtons={buttons}
        menu={<OpportunityMenu />}
        breadcrumbs={<SingleOpportunityBreadcrumbs />}
        entityHeader={<OpportunityEntityHeader />}
        hasSeparateSections
      >
        <ShowSection isVisible={data => data.getValueBySource('crmPipeline')}>
          <OpportunityPipeline />
        </ShowSection>
        <ShowSection title="DocuSign signing status">
          <DocuSignEnvelopeBox source="envelopes" />
        </ShowSection>
        <ShowSection title="Opportunity information">
          <SectionRow>
            <AccountLinkField label="Account name" idSource="accountId" source="accountName" isInternal={false} />
            <OpportunityTypeField label="Opportunity type" source="type" />
            <OpportunityStageField label="Opportunity state" source="stateStep" />
            <OpportunityProbabilityField label="Probability" source="stateStep" />
            <TextField label="Opportunity ID" source="id" />
            <OpportunityLinkField
              source="afterDiscoveryOpportunityName"
              idSource="afterDiscoveryOpportunityId"
              label="After discovery opportunity"
              isVisible={(data: any) => data.getValueBySource('afterDiscoveryOpportunityId')}
            />
            <OpportunityLinkField
              source="discoveryOpportunityName"
              idSource="discoveryOpportunityId"
              label="Discovery opportunity"
              isVisible={(data: any) => data.getValueBySource('discoveryOpportunityId')}
            />
            <DateField label="Close date" source="closeDate" />
            <DateField
              label="Postponed until"
              source="postponedUntil"
              isVisible={(data: any) => data.getValueBySource('postponedUntil')}
            />
            <TextField label="Opportunity owner" source="ownerName" />
            <TextField label="Presale coordinator" source="presaleCoordinatorName" />
            <BooleanOptionField source="isContractRenewal" label="Contract renewal" />
            <LeadLinkField
              label="Converted from"
              idSource="convertedFromId"
              nameSource="convertedFromName"
              isVisible={(data: any) => data.getValueBySource('convertedFromId')}
            />
            <NewProjectNameField />
            <ClosedReasonField
              isVisible={isClosedReasonVisible}
              label="Primary reason for closed status"
              source="closedReason.primaryReason"
            />
            <ClosedReasonField
              isVisible={isClosedReasonVisible}
              label="Secondary reason for closed status"
              source="closedReason.secondaryReason"
            />
          </SectionRow>
          <BigSectionRow>
            <ReplaceUrlWithLink isVisible={isClosedReasonVisible}>
              <HtmlField label="Closed summary" source="closedReason.summary" />
            </ReplaceUrlWithLink>
          </BigSectionRow>
        </ShowSection>

        <ShowSection title="Contact information">
          <SectionRow>
            <ContactLinkField
              label="Primary contact"
              linkText={(data: any) =>
                data.getValueBySource('primaryContact') && data.getValueBySource('primaryContact.fullName')
              }
              idSource="primaryContact.id"
              isInternal={false}
            />
            <OpportunityPrimaryContactPhoneField
              label="Phone numbers"
              phoneNumberSource="primaryContact.phoneNumber"
              mobilePhoneNumberSource="primaryContact.mobilePhoneNumber"
            />
            <ListField source="primaryContact.emails">
              <EmailField />
            </ListField>
          </SectionRow>
        </ShowSection>

        <ShowSection title="Financial information">
          <SectionRow>
            <AmountField label="Max. total cost (USD)" source="upperLimit" />
            <AmountField label="Most expected total cost (USD)" source="mostExpected" />
            <AmountField label="Min. recommended cost (USD)" source="lowerLimit" />

            <OpportunityExpectedRevenueField
              label="Expected revenue (USD)"
              stageSource="stage"
              amountSource="mostExpected"
            />
          </SectionRow>
        </ShowSection>

        <ShowSection title="Description information">
          <BigSectionRow>
            <ReplaceUrlWithLink>
              <HtmlField label="" source="description" />
            </ReplaceUrlWithLink>
          </BigSectionRow>
        </ShowSection>

        <ShowSection title="Details for presale phase" isVisible={data => data.getValueBySource('preSaleInput')}>
          <BigSectionRow>
            <ReplaceUrlWithLink>
              <HtmlField label="" source="preSaleInput" />
            </ReplaceUrlWithLink>
          </BigSectionRow>
        </ShowSection>

        <ShowSection title="Presale output" isVisible={data => data.getValueBySource('preSaleOutput')}>
          <BigSectionRow>
            <ReplaceUrlWithLink>
              <HtmlField label="" source="preSaleOutput" />
            </ReplaceUrlWithLink>
          </BigSectionRow>
        </ShowSection>

        <ShowSection title="Additional information">
          <SectionRow>
            <TextField label="Next step" source="nextStep" />
            <DateField label="Next step deadline" source="nextStepDeadline" />
          </SectionRow>
        </ShowSection>

        <ShowSection title="Attachments">
          <BigSectionRow>
            <CompositeDocumentListField artifactsListSource={'attachmentsIds'} documentsListsSource={'artifactPaths'} />
          </BigSectionRow>
        </ShowSection>
        <ShowSection
          title="Attachments for presale"
          isVisible={data => {
            const relatedData = data.getValueBySource('preSaleInputAttachmentsIds');
            return relatedData && relatedData.length > 0;
          }}
        >
          <BigSectionRow>
            <AttachmentsListField label="" source="preSaleInputAttachmentsIds" />
          </BigSectionRow>
        </ShowSection>
        <ShowSection
          title="Presale result attachments"
          isVisible={data => {
            const relatedData = data.getValueBySource('preSaleOutputAttachmentsIds');
            return relatedData && relatedData.length > 0;
          }}
        >
          <BigSectionRow>
            <AttachmentsListField label="" source="preSaleOutputAttachmentsIds" />
          </BigSectionRow>
        </ShowSection>
      </ShowPageLayout>
    </Show>
  );
};

export default OpportunityViewPage;
